import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import {
  ArrowLeft, ArrowRight, BadgeCheck, Check, CheckCircle2, ChevronDown, Clock, MapPin,
  Phone, Store, Wallet, X,
} from "lucide-react";
import { aggregatorApi, formatApiError } from "../lib/api";
import { SuccessSound } from "./SiteSound";
import { CANCELLATION_POLICY } from "../config/defaults";

const STEP_LABELS = ["Centre", "Service", "Vehicle", "Schedule", "Details", "Confirm", "Booked"];
const emptyDetails = { customer_name: "", customer_phone: "", vehicle_number: "", notes: "", company_website: "" };

export const prettyDate = (iso) => {
  const d = new Date(`${iso}T00:00:00`);
  return d.toLocaleDateString("en-IN", { weekday: "short", day: "numeric", month: "short" });
};

export const prettySlot = (s) => {
  const [h, m] = s.split(":").map(Number);
  const am = h >= 12 ? "PM" : "AM";
  return `${((h + 11) % 12) + 1}:${String(m).padStart(2, "0")} ${am}`;
};

export const directionsUrl = (c) =>
  `https://www.google.com/maps/search/?api=1&query=${c.lat && c.lng ? `${c.lat},${c.lng}` : encodeURIComponent(`${c.name || ""} ${c.area || c.address || ""}`)}`;

export default function AggregatorBookingModal({ open, centre, onClose }) {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [selected, setSelected] = useState(null);
  const [service, setService] = useState("");
  const [vehicle, setVehicle] = useState("");
  const [date, setDate] = useState("");
  const [slot, setSlot] = useState("");
  const [details, setDetails] = useState(emptyDetails);
  const [dates, setDates] = useState([]);
  const [slots, setSlots] = useState([]);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [slotsNote, setSlotsNote] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (open) {
      setSelected(centre || null);
      setStep(centre ? 1 : 0);
      setService(""); setVehicle(""); setDate(""); setSlot("");
      setDetails(emptyDetails); setResult(null); setError("");
      if (window.__lenis) window.__lenis.stop();
      document.body.style.overflow = "hidden";
    } else {
      if (window.__lenis) window.__lenis.start();
      document.body.style.overflow = "";
    }
  }, [open, centre]);

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const loadSlots = async (code, d) => {
    setSlotsLoading(true); setSlotsNote("");
    try {
      const res = await aggregatorApi.slots(code, d);
      setSlots(res.slots || []);
      if (!res.availability_configured) setSlotsNote(res.note || "Availability to be confirmed");
    } catch (e) {
      setSlots([]);
      setSlotsNote(formatApiError(e, "Could not load slots. Please try again."));
    } finally {
      setSlotsLoading(false);
    }
  };

  const pickDate = async (d) => {
    setDate(d); setSlot(""); setError("");
    await loadSlots(selected.centre_code, d);
  };

  const valid = [
    !!selected,
    !!service,
    !!vehicle,
    !!date && !!slot,
    details.customer_name.trim().length >= 2 && details.customer_phone.replace(/\D/g, "").length === 10,
  ][step];

  const back = () => { setError(""); setStep((s) => Math.max(0, s - 1)); };
  const next = () => { setError(""); setStep((s) => Math.min(5, s + 1)); };

  const confirm = async () => {
    setLoading(true); setError("");
    try {
      const res = await aggregatorApi.createBooking({
        centre_code: selected.centre_code, service, vehicle_category: vehicle,
        date, time_slot: slot, ...details,
      });
      setResult(res);
      sessionStorage.setItem("carza_agg_booking", JSON.stringify({ ref: res.booking_ref, phone: details.customer_phone.replace(/\D/g, "").slice(-10) }));
      setStep(6);
    } catch (e) {
      setError(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  if (!open) return null;

  return (
    <motion.div className="fixed inset-0 z-[100] flex items-end justify-center sm:items-center sm:p-6"
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      role="dialog" aria-modal="true" aria-label="Book a car wash slot" data-testid="agg-modal">
      <div className="absolute inset-0 bg-[#0d0620]/75 backdrop-blur-sm" onClick={onClose} />
      <motion.div data-lenis-prevent
        className="relative flex max-h-[94vh] w-full max-w-lg flex-col overflow-hidden rounded-t-3xl border border-white/10 bg-graphite sm:rounded-3xl"
        initial={{ y: 60, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 60, opacity: 0 }}
        transition={{ type: "spring", damping: 28, stiffness: 300 }}>
        <div className="flex items-center justify-between border-b border-white/5 px-6 py-4">
          <div className="flex items-center gap-3">
            <span className="font-logo text-[11px] tracking-[0.2em] text-paper">carza</span>
            <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-mist-2">
              Step {step + 1} of 6 — {STEP_LABELS[step]}
            </span>
          </div>
          <button onClick={onClose} aria-label="Close" className="flex h-8 w-8 items-center justify-center rounded-full border border-white/10 text-mist hover:text-paper" data-testid="agg-close-btn">
            <X size={15} />
          </button>
        </div>
        <div className="h-[3px] w-full bg-white/5">
          <motion.div className="h-full bg-carza" animate={{ width: `${((step + 1) / 7) * 100}%` }} transition={{ duration: 0.4 }} />
        </div>

        <div className="overflow-y-auto overscroll-contain px-6 py-6" data-testid="agg-modal-body">
          <AnimatePresence mode="wait">
            <motion.div key={step} initial={{ opacity: 0, x: 24 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -24 }} transition={{ duration: 0.28 }}>
              {step === 0 && <StepCentre selected={selected} onSelect={(c) => { setSelected(c); setError(""); }} />}
              {step === 1 && <StepService centre={selected} service={service} onPick={setService} />}
              {step === 2 && <StepVehicle centre={selected} vehicle={vehicle} onPick={setVehicle} />}
              {step === 3 && (
                <StepSchedule centre={selected} dates={dates} setDates={setDates} date={date} slot={slot}
                  slots={slots} slotsLoading={slotsLoading} slotsNote={slotsNote}
                  onPickDate={pickDate} onPickSlot={(s) => { setSlot(s); setError(""); }} />
              )}
              {step === 4 && <StepDetails details={details} setDetails={setDetails} />}
              {step === 5 && (
                <StepConfirm centre={selected} service={service} vehicle={vehicle} date={date} slot={slot}
                  details={details} error={error} loading={loading} onConfirm={confirm} />
              )}
              {step === 6 && (
                <Success result={result} onView={() => { onClose(); navigate(`/booking/${result.booking_ref}`); }}
                  onDirections={() => window.open(directionsUrl(selected), "_blank")}
                  onHome={() => { onClose(); navigate("/"); }} />
              )}
            </motion.div>
          </AnimatePresence>
        </div>

        {step <= 4 && (
          <div className="flex items-center justify-between gap-3 border-t border-white/5 px-6 py-4">
            <button onClick={back} disabled={step === 0 || (step === 1 && centre)} className="btn btn-ghost px-4 py-2 text-[13px] disabled:opacity-30" data-testid="agg-back-btn">
              <ArrowLeft size={15} /> Back
            </button>
            <span className="rounded-full border border-white/10 px-3 py-1.5 font-mono text-[9px] uppercase tracking-[0.18em] text-mist-2">Pay at centre</span>
            <button onClick={next} disabled={!valid} className="btn btn-primary px-6 py-3 disabled:cursor-not-allowed disabled:opacity-40" data-testid="agg-next-btn">
              Continue <ArrowRight size={15} />
            </button>
          </div>
        )}
        {step === 5 && (
          <div className="border-t border-white/5 px-6 py-4">
            <button onClick={back} className="btn btn-ghost px-4 py-2 text-[13px]" data-testid="agg-back-btn-confirm">
              <ArrowLeft size={15} /> Back
            </button>
          </div>
        )}
      </motion.div>
    </motion.div>
  );
}

function StepCentre({ selected, onSelect }) {
  const [centres, setCentres] = useState(null);
  useEffect(() => {
    aggregatorApi.centres().then((r) => setCentres((r.centres || []).filter((c) => c.availability_configured))).catch(() => setCentres([]));
  }, []);
  return (
    <div className="flex flex-col gap-3">
      <h3 className="font-display text-2xl font-bold text-paper">Select a centre</h3>
      <p className="text-sm text-mist">Centres with live slot booking. Pay the centre directly after your wash.</p>
      {!centres && <p className="py-8 text-center text-sm text-mist-2">Loading centres…</p>}
      {centres && centres.length === 0 && (
        <p className="rounded-2xl border border-amber-400/25 bg-amber-400/[0.07] px-4 py-3 text-sm text-amber-200">
          No centres have open slot booking yet. Please check back soon.
        </p>
      )}
      {centres && centres.map((c) => (
        <button key={c.centre_code} onClick={() => onSelect(c)}
          className={`card-surface flex w-full items-center justify-between gap-3 rounded-2xl px-5 py-4 text-left transition-all ${selected?.centre_code === c.centre_code ? "!border-carza/60 shadow-[0_0_30px_-10px_rgba(124,77,255,0.5)]" : "hover:-translate-y-0.5"}`}
          data-testid={`agg-centre-option-${c.centre_code}`}>
          <span className="flex items-center gap-3">
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-carza-dim text-carza-lite"><Store size={17} /></span>
            <span>
              <span className="block text-[15px] font-semibold text-paper">{c.name}</span>
              <span className="mt-0.5 flex items-center gap-1 text-xs text-mist"><MapPin size={10} /> {c.area || c.address}</span>
            </span>
          </span>
          {selected?.centre_code === c.centre_code && <span className="flex h-5 w-5 items-center justify-center rounded-full border border-carza bg-carza"><Check size={12} className="text-white" /></span>}
        </button>
      ))}
    </div>
  );
}

function StepService({ centre, service, onPick }) {
  return (
    <div className="flex flex-col gap-3">
      <h3 className="font-display text-2xl font-bold text-paper">Select a service</h3>
      <p className="flex items-center gap-1.5 text-xs text-mist-2"><BadgeCheck size={12} className="text-carza-lite" /> Offered by {centre?.name}</p>
      {(centre?.services || []).map((s) => (
        <button key={s} onClick={() => onPick(s)}
          className={`card-surface flex w-full items-center justify-between rounded-2xl px-5 py-4 text-left text-[15px] font-semibold text-paper transition-all ${service === s ? "!border-carza/60 shadow-[0_0_30px_-10px_rgba(124,77,255,0.5)]" : "hover:-translate-y-0.5"}`}
          data-testid={`agg-service-${s.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
          {s}
          {service === s && <span className="flex h-5 w-5 items-center justify-center rounded-full bg-carza"><Check size={12} className="text-white" /></span>}
        </button>
      ))}
    </div>
  );
}

function StepVehicle({ centre, vehicle, onPick }) {
  return (
    <div className="flex flex-col gap-3">
      <h3 className="font-display text-2xl font-bold text-paper">Select vehicle type</h3>
      <div className="mt-1 grid grid-cols-2 gap-2">
        {(centre?.vehicle_categories || []).map((v) => (
          <button key={v} onClick={() => onPick(v)}
            className={`rounded-2xl border px-4 py-3.5 text-[14px] font-medium transition-all ${vehicle === v ? "border-carza/60 bg-carza-dim text-carza-lite" : "border-white/10 text-mist hover:border-white/25"}`}
            data-testid={`agg-vehicle-${v.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
            {v}
          </button>
        ))}
      </div>
    </div>
  );
}

function StepSchedule({ centre, dates, setDates, date, slot, slots, slotsLoading, slotsNote, onPickDate, onPickSlot }) {
  const [datesError, setDatesError] = useState("");
  useEffect(() => {
    if (dates.length || !centre) return;
    setDatesError("");
    aggregatorApi.availability(centre.centre_code)
      .then((r) => setDates(r.dates || []))
      .catch(() => setDatesError("Could not load availability — please close and reopen, or try again shortly."));
  }, [centre, dates.length]);
  const todayIso = new Date().toLocaleDateString("en-CA", { timeZone: "Asia/Kolkata" });
  return (
    <div className="flex flex-col gap-4">
      <h3 className="font-display text-2xl font-bold text-paper">Pick a date &amp; slot</h3>
      {datesError && <p className="rounded-xl border border-amber-400/25 bg-amber-400/[0.07] px-4 py-3 text-[13px] text-amber-200" data-testid="agg-dates-error">{datesError}</p>}
      <div>
        <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-mist-2">Date</span>
        <div className="no-scrollbar mt-2 flex gap-2 overflow-x-auto pb-1">
          {dates.map((d) => (
            <button key={d} onClick={() => onPickDate(d)}
              className={`shrink-0 rounded-xl border px-4 py-2.5 text-[13px] font-medium transition-all ${date === d ? "border-carza/60 bg-carza-dim text-carza-lite" : "border-white/10 text-mist hover:border-white/25"}`}
              data-testid={`agg-date-${d}`}>
              {d === todayIso ? `Today · ${prettyDate(d)}` : prettyDate(d)}
            </button>
          ))}
        </div>
      </div>
      <div>
        <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-mist-2">Time slot</span>
        {!date && <p className="mt-3 rounded-xl border border-white/10 px-4 py-3 text-[13px] text-mist">Pick a date to see available slots.</p>}
        {slotsLoading && <p className="mt-3 text-[13px] text-mist-2">Checking availability…</p>}
        {slotsNote && !slotsLoading && <p className="mt-3 rounded-xl border border-amber-400/25 bg-amber-400/[0.07] px-4 py-3 text-[13px] text-amber-200" data-testid="agg-slots-note">{slotsNote}</p>}
        {!slotsLoading && date && !slotsNote && (
          <div className="mt-2 grid grid-cols-3 gap-2">
            {slots.map((s) => (
              <button key={s.slot} onClick={() => s.available && onPickSlot(s.slot)} disabled={!s.available}
                className={`rounded-xl border px-2 py-2.5 text-center transition-all ${slot === s.slot ? "border-carza/60 bg-carza-dim text-carza-lite" : s.available ? "border-white/10 text-mist hover:border-white/25" : "cursor-not-allowed border-white/5 text-mist-2/40"}`}
                data-testid={`agg-slot-${s.slot.replace(":", "-")}`}>
                <span className="flex items-center justify-center gap-1 text-[13px] font-medium"><Clock size={10} /> {prettySlot(s.slot)}</span>
                <span className="mt-0.5 block text-[9px] uppercase tracking-wider text-mist-2">{s.available ? `${s.remaining} left` : "Full"}</span>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function StepDetails({ details, setDetails }) {
  const set = (k) => (e) => setDetails((d) => ({ ...d, [k]: e.target.value }));
  return (
    <div className="flex flex-col gap-4">
      <h3 className="font-display text-2xl font-bold text-paper">Your details</h3>
      <label className="flex flex-col gap-1.5">
        <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-mist-2">Full name <span className="text-carza">*</span></span>
        <input className="field" value={details.customer_name} onChange={set("customer_name")} placeholder="Your name" autoComplete="name" data-testid="agg-name-input" />
      </label>
      <label className="flex flex-col gap-1.5">
        <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-mist-2">Mobile number <span className="text-carza">*</span></span>
        <input className="field" value={details.customer_phone} onChange={set("customer_phone")} placeholder="10-digit mobile number" inputMode="tel" autoComplete="tel" data-testid="agg-phone-input" />
      </label>
      <label className="flex flex-col gap-1.5">
        <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-mist-2">Vehicle number <span className="text-mist-2/60">(optional)</span></span>
        <input className="field" value={details.vehicle_number} onChange={set("vehicle_number")} placeholder="e.g. UP16 AB 1234" data-testid="agg-vehicle-number-input" />
      </label>
      <label className="flex flex-col gap-1.5">
        <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-mist-2">Booking notes <span className="text-mist-2/60">(optional)</span></span>
        <textarea className="field min-h-[70px] resize-none" value={details.notes} onChange={set("notes")} placeholder="Anything the centre should know…" data-testid="agg-notes-input" />
      </label>
      <input type="text" name="company_website" value={details.company_website} onChange={set("company_website")} className="hidden" tabIndex={-1} autoComplete="off" aria-hidden="true" />
    </div>
  );
}

function StepConfirm({ centre, service, vehicle, date, slot, details, error, loading, onConfirm }) {
  const rows = [
    ["Centre", `${centre?.name} · ${centre?.area || centre?.address}`],
    ["Service", service],
    ["Vehicle", `${vehicle}${details.vehicle_number ? ` · ${details.vehicle_number}` : ""}`],
    ["Date & time", `${prettyDate(date)}, ${prettySlot(slot)}`],
    ["Customer", `${details.customer_name} · +91 ${details.customer_phone.replace(/\D/g, "").slice(-10)}`],
  ];
  return (
    <div className="flex flex-col gap-4">
      <h3 className="font-display text-2xl font-bold text-paper">Confirm your booking</h3>
      <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-4 text-sm text-mist" data-testid="agg-summary">
        {rows.map(([k, v]) => (
          <div key={k} className="flex justify-between gap-6 py-1.5">
            <span>{k}</span><span className="text-right text-paper">{v}</span>
          </div>
        ))}
        <div className="mt-2 flex items-start justify-between gap-4 border-t border-white/10 pt-3">
          <span>Payment</span>
          <span className="max-w-[60%] text-right text-[13px] font-medium text-carza-lite" data-testid="agg-payment-note">
            No payment now — pay the washing centre directly after your car wash
          </span>
        </div>
      </div>
      <details className="rounded-2xl border border-white/10 bg-white/[0.02] px-4 py-3" data-testid="agg-policy">
        <summary className="flex cursor-pointer items-center justify-between text-[13px] font-semibold text-paper">
          Cancellation Policy <ChevronDown size={14} className="text-mist-2" />
        </summary>
        <ul className="mt-3 flex flex-col gap-2">
          {CANCELLATION_POLICY.map((p) => (
            <li key={p} className="flex items-start gap-2 text-[12px] leading-relaxed text-mist">
              <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-carza" /> {p}
            </li>
          ))}
        </ul>
      </details>
      {error && <p className="rounded-xl border border-red-400/30 bg-red-400/10 px-4 py-3 text-sm text-red-300" data-testid="agg-error">{error}</p>}
      <button onClick={onConfirm} disabled={loading} className="btn btn-primary w-full py-4 disabled:opacity-50" data-testid="agg-confirm-btn">
        {loading ? "Booking your slot…" : <>Confirm Booking <ArrowRight size={15} /></>}
      </button>
    </div>
  );
}

function Success({ result, onView, onDirections, onHome }) {
  const rows = [
    ["Booking reference", result?.booking_ref],
    ["Centre", `${result?.centre_name} · ${result?.centre_area}`],
    ["Service", result?.service],
    ["Date & time", `${prettyDate(result?.date)}, ${prettySlot(result?.time_slot)}`],
    ["Centre contact", result?.centre_phone || "—"],
    ["Payment method", "Pay the centre directly after service"],
  ];
  return (
    <div className="flex flex-col items-center gap-5 py-2 text-center" data-testid="agg-success">
      <SuccessSound />
      <motion.div initial={{ scale: 0.6, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ type: "spring", damping: 14 }}
        className="flex h-16 w-16 items-center justify-center rounded-full bg-carza-dim text-carza-lite">
        <CheckCircle2 size={30} />
      </motion.div>
      <div>
        <h3 className="font-display text-2xl font-bold text-paper">Your Slot Is Booked!</h3>
        <p className="mx-auto mt-2 max-w-sm text-sm leading-relaxed text-mist">
          Your car wash has been scheduled successfully. Please pay the washing centre directly after the service is completed.
        </p>
      </div>
      <div className="w-full rounded-2xl border border-white/10 bg-white/[0.03] p-5 text-left text-sm" data-testid="agg-success-details">
        {rows.map(([k, v]) => (
          <div key={k} className="flex justify-between gap-6 border-t border-white/5 py-2.5 first:border-t-0">
            <span className="shrink-0 text-mist">{k}</span>
            <span className="text-right font-medium text-paper">{v}</span>
          </div>
        ))}
      </div>
      <div className="flex w-full flex-col gap-2.5">
        <button onClick={onView} className="btn btn-primary w-full py-3.5" data-testid="agg-view-booking-btn">View Booking <ArrowRight size={15} /></button>
        <button onClick={onDirections} className="btn btn-outline w-full py-3.5" data-testid="agg-directions-btn"><MapPin size={15} /> Get Directions</button>
        <button onClick={onHome} className="btn btn-ghost w-full py-3" data-testid="agg-home-btn">Back to Home</button>
      </div>
    </div>
  );
}
